import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import { doctors } from "@/data/doctors";

const trustItems = [
  {
    icon: "ri-user-star-line",
    title: "Specialist Care",
    text: "Senior consultant-led orthopaedic and spine surgery for every patient.",
    card: "border-primary-100 bg-primary-50",
    bubble: "bg-primary-100 text-primary-600",
  },
  {
    icon: "ri-focus-3-line",
    title: "Personalised Approach",
    text: "Accurate diagnosis and a treatment plan designed around your condition.",
    card: "border-secondary-100 bg-secondary-50",
    bubble: "bg-secondary-100 text-secondary-600",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Comprehensive Care",
    text: "From first consultation through surgery, recovery and rehabilitation.",
    card: "border-accent-100 bg-accent-50",
    bubble: "bg-accent-100 text-accent-600",
  },
];

export default function Hero() {
  // The hero photograph is driven entirely by the centralized doctor data.
  // To replace the photo later, change only the `image` value in src/data/doctors.ts.
  const doctor = doctors[0];

  return (
    <section id="home" className="relative scroll-mt-28 overflow-hidden bg-background-50">
      {/* Soft multi-colour canvas — blue, cyan, violet washes */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50"
        aria-hidden="true"
      ></div>
      <div
        className="absolute inset-0 opacity-[0.4] [background-image:linear-gradient(to_right,oklch(var(--background-200))_1px,transparent_1px),linear-gradient(to_bottom,oklch(var(--background-200))_1px,transparent_1px)] [background-size:72px_72px]"
        aria-hidden="true"
      ></div>

      {/* Decorative colour glows */}
      <div className="absolute -right-24 -top-20 h-[34rem] w-[34rem] rounded-full bg-accent-200/40 blur-3xl" aria-hidden="true"></div>
      <div className="absolute -bottom-40 -left-24 h-[30rem] w-[30rem] rounded-full bg-primary-200/45 blur-3xl" aria-hidden="true"></div>
      <div className="absolute left-1/3 top-1/4 h-72 w-72 rounded-full bg-secondary-200/35 blur-3xl" aria-hidden="true"></div>

      <div className="relative mx-auto max-w-7xl px-4 pb-14 pt-12 md:px-6 md:pb-16 md:pt-16">
        <div className="grid grid-cols-1 items-center gap-x-12 gap-y-14 lg:grid-cols-12">
          {/* Left — editorial headline, intro, CTAs */}
          <div className="lg:col-span-6">
            <span className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-secondary-50/80 px-3.5 py-1.5 font-label text-xs font-semibold uppercase tracking-[0.16em] text-secondary-700 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-secondary-500 to-primary-500" aria-hidden="true"></span>
              Orthopaedic &amp; Spine Specialists · Trichy
            </span>

            <h1 className="animate-fade-up mt-6 font-heading text-[2.5rem] font-semibold leading-[1.04] tracking-tight text-foreground-950 md:text-6xl">
              Advanced
              <span className="italic text-primary-600"> Orthopaedic</span>
              <br />
              &amp; <span className="text-gradient-brand">Spine Care</span>
            </h1>

            <p className="animate-fade-up mt-6 max-w-xl font-body text-base leading-relaxed text-foreground-600 md:text-lg">
              Expert-led care for your bones, joints and spine — designed around you. Senior-consultant diagnosis and
              treatment at Ortho Spine Care, Srirangam, Trichy.
            </p>

            <div className="animate-fade-up mt-8 flex flex-col gap-3 sm:flex-row">
              <Button to="/appointment" size="lg" icon="ri-calendar-check-line">
                Book Appointment
              </Button>
              <Button to="/services" variant="outline" size="lg" icon="ri-compass-3-line">
                Explore Our Care
              </Button>
            </div>

            <div className="animate-fade-up mt-7 flex items-center gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-700">
                <i className="ri-stethoscope-line text-lg" aria-hidden="true"></i>
              </span>
              <p className="font-label text-sm text-foreground-700">
                Led by <strong className="font-semibold text-foreground-950">{doctor.name}</strong>
                <span className="text-foreground-500"> · {doctor.designation}</span>
              </p>
            </div>
          </div>

          {/* Right — layered doctor composition with colourful shapes */}
          <div className="lg:col-span-6">
            <div className="relative mx-auto w-full max-w-[440px]">
              <div className="absolute -inset-x-6 -bottom-8 top-8 rounded-[2.75rem] bg-gradient-to-br from-primary-200/70 via-secondary-200/60 to-accent-200/70" aria-hidden="true"></div>
              <div className="absolute -left-8 top-6 h-24 w-24 rounded-full bg-secondary-200/70" aria-hidden="true"></div>
              <div className="absolute -right-4 -top-6 h-20 w-20 rounded-full border border-accent-200 bg-accent-100/70" aria-hidden="true"></div>
              <div className="absolute -left-6 bottom-28 h-14 w-14 rounded-full bg-primary-200/80" aria-hidden="true"></div>

              <div className="relative overflow-hidden rounded-[2.5rem] border border-background-200 bg-background-100 shadow-[0_35px_80px_-45px_rgba(15,40,80,0.5)]">
                <div className="h-[440px] w-full md:h-[560px]">
                  <img
                    src={doctor.image}
                    alt={doctor.imageAlt}
                    title={`${doctor.name} — ${doctor.specialty}, Trichy`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="absolute right-4 top-4 flex items-center gap-2 rounded-full border border-background-200/80 bg-background-50/90 px-3 py-1.5 backdrop-blur">
                  <i className="ri-shield-check-line text-primary-600" aria-hidden="true"></i>
                  <span className="font-label text-xs font-semibold text-foreground-800">Senior Consultant</span>
                </div>
              </div>

              {/* Floating doctor card */}
              <Link
                to="/about#specialist"
                className="absolute -bottom-7 -left-3 flex w-[15.5rem] cursor-pointer items-center gap-3 rounded-2xl border border-background-200 bg-background-50/95 p-3 shadow-[0_20px_45px_-28px_rgba(15,40,80,0.55)] backdrop-blur transition-transform duration-300 hover:-translate-y-1 md:-left-8"
              >
                <span className="h-12 w-12 flex-none overflow-hidden rounded-full border border-background-200">
                  <img
                    src={doctor.image}
                    alt={doctor.imageAlt}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-heading text-sm font-semibold text-foreground-950">
                    {doctor.name}
                  </span>
                  <span className="block truncate font-label text-xs text-foreground-600">{doctor.specialty}</span>
                  <span className="text-gradient-brand mt-0.5 inline-flex items-center gap-1 font-label text-xs font-semibold">
                    About Your Specialist
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </span>
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Hero trust indicators — each with its own soft colour */}
        <div className="mt-16 grid grid-cols-1 gap-4 border-t border-background-200 pt-8 md:grid-cols-3">
          {trustItems.map((item) => (
            <div
              key={item.title}
              className={`flex items-start gap-4 rounded-xl border p-5 ${item.card}`}
            >
              <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-md ${item.bubble}`}>
                <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-1 font-body text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}